import { Injectable } from '@angular/core';
import { clearTranslations, loadTranslations } from '@angular/localize';
import { enUS, es, ru, uk } from 'date-fns/locale';
import { Subject } from 'rxjs';

import localizeEn from '../../assets/i18n/app.localize.en.json';
import localizeEs from '../../assets/i18n/app.localize.es.json';
import localizeRu from '../../assets/i18n/app.localize.ru.json';
import localizeUk from '../../assets/i18n/app.localize.uk.json';

interface AppLocalizePack {
  translations: Record<string, string>;
}

export type UiLang = 'ru' | 'uk' | 'en' | 'es';

/** One row for login / settings language pickers. */
export interface UiLanguageOption {
  id: UiLang;
  /** Short tag shown on FAB and list subtitle (UA for Ukrainian avoids “UK” / United Kingdom). */
  code: string;
  flagAsset: string;
  nativeLabel: string;
}

/** Single source for supported UI languages, display codes, and flag assets (order = picker order). */
export const UI_LANGUAGE_OPTIONS: readonly UiLanguageOption[] = [
  {
    id: 'uk',
    code: 'UA',
    flagAsset: 'assets/flags/ua.svg',
    nativeLabel: 'Українська',
  },
  {
    id: 'ru',
    code: 'RU',
    flagAsset: 'assets/flags/ru.svg',
    nativeLabel: 'Русский',
  },
  {
    id: 'en',
    code: 'EN',
    flagAsset: 'assets/flags/us.svg',
    nativeLabel: 'English',
  },
  {
    id: 'es',
    code: 'ES',
    flagAsset: 'assets/flags/es.svg',
    nativeLabel: 'Español',
  },
] as const;

/** Maps API/store `terminal.lang` (and persisted UI choice) to a loaded bundle key. */
function normalizeTerminalLang(raw: unknown): UiLang {
  const s = String(raw ?? '')
    .toLowerCase()
    .trim();
  const found = UI_LANGUAGE_OPTIONS.find((o) => o.id === s);
  return found ? found.id : 'uk';
}

/** Switches UI language via Angular `$localize` / `loadTranslations` only (no ngx JSON HTTP API). */
@Injectable({ providedIn: 'root' })
export class LanguagesService {
  private static readonly _uiLangStorageKey = '__app_ui_lang';

  defaultLeng = 'ru';
  private _currentLang: UiLang = 'uk';

  private readonly _languageChange = new Subject<void>();
  /** Emits when UI language changes (for Angular $localize / template refresh). */
  readonly onLanguageChange = this._languageChange.asObservable();

  /** Same order as `UI_LANGUAGE_OPTIONS` (readonly reference). */
  readonly uiLanguageOptions = UI_LANGUAGE_OPTIONS;

  constructor() {
    try {
      const saved = localStorage.getItem(LanguagesService._uiLangStorageKey);
      if (saved !== null && saved !== '') {
        this._currentLang = normalizeTerminalLang(saved);
      }
    } catch {
      /* ignore quota / SSR */
    }
    this._applyAppLocalize(this._currentLang);
  }

  /**
   * Short display code for a UI language (`UA`, `RU`, …).
   * Defaults to the active language.
   */
  getUiLangDisplayCode(lang?: UiLang): string {
    const id = lang ?? this._currentLang;
    const o = UI_LANGUAGE_OPTIONS.find((x) => x.id === id);
    return o?.code ?? 'UA';
  }

  getDateLocaleSetting() {
    const lang = this.getCurrentLang();
    if (lang === 'ru') {
      return { locale: ru };
    }
    if (lang === 'uk') {
      return { locale: uk };
    }
    if (lang === 'en') {
      return { locale: enUS };
    }
    if (lang === 'es') {
      return { locale: es };
    }
    return {};
  }

  /** Sets UI bundle from terminal API, login picker, etc. Unknown codes fall back to Ukrainian. */
  setLanguage(lang: UiLang | string) {
    this._currentLang = normalizeTerminalLang(lang);
    try {
      localStorage.setItem(
        LanguagesService._uiLangStorageKey,
        this._currentLang,
      );
    } catch {
      /* ignore quota / SSR */
    }
    this._applyAppLocalize(this._currentLang);
    this._languageChange.next();
  }

  getCurrentLang(): UiLang {
    return this._currentLang;
  }

  getCulture() {
    console.log('getCulture');

    switch (this._currentLang) {
      case 'uk':
        return 'uk-UA';
      case 'ru':
        return 'ru-RU';
      case 'en':
        return 'en-US';
      case 'es':
        return 'es-ES';

      default:
        return 'uk-UA';
    }
  }

  private _applyAppLocalize(lang: UiLang) {
    clearTranslations();
    const pack: AppLocalizePack =
      lang === 'ru'
        ? (localizeRu as AppLocalizePack)
        : lang === 'en'
        ? (localizeEn as AppLocalizePack)
        : lang === 'es'
        ? (localizeEs as AppLocalizePack)
        : (localizeUk as AppLocalizePack);
    loadTranslations(pack.translations);
  }
}
